import type { TFunction } from 'i18next';

import type { TImporterState } from '@feathr/blackbox';

interface IViewContactsTooltipParams {
  /** Whether the import produced a group to view the contacts in. */
  hasGroup: boolean;
  /** Whether the import left behind an error file, which adds a 'Download Errors' item. */
  hasErrors: boolean;
  state?: TImporterState;
}

/*
 * 'View Contacts' is disabled whenever there is no group to open, which happens for
 * four different reasons — the import hasn't run, is still running, failed, or
 * completed without matching anyone. Each gets its own reason, because telling
 * someone with a queued import that "no contacts were imported" would be wrong.
 */
function getDisabledReason(state: TImporterState | undefined, t: TFunction): string {
  switch (state) {
    case 'draft':
      return t("This import hasn't run yet, so there are no contacts to view.");

    case 'pending':
      return t('This import is still running. Contacts will be available when it finishes.');

    case 'failed':
      return t('This import failed, so no contacts were added.');

    /*
     * 'complete' reaches here only when the import finished without a group. Any
     * unknown state lands here too: a completed-but-empty import is the safest
     * reading of "there is no group", since the other three states are explicit.
     */
    default:
      return t('No contacts were imported.');
  }
}

/**
 * The tooltip explaining why 'View Contacts' is disabled, or `undefined` when the
 * item is enabled and needs no explanation.
 */
export function getViewContactsTooltip(
  { hasGroup, hasErrors, state }: IViewContactsTooltipParams,
  t: TFunction,
): string | undefined {
  const isViewable = state === 'complete' && hasGroup;
  if (isViewable) {
    return undefined;
  }

  const reason = getDisabledReason(state, t);
  if (!hasErrors) {
    return reason;
  }

  // 'Download Errors' sits directly below 'View Contacts' in the same menu.
  return `${reason} ${t('See Download Errors below for details.')}`;
}
